import {
	BASELINE_GRAPHQL_DATA,
	MICRO_LAUNCH_GRAPHQL_DATA,
	PREWORKSHOP_GRAPHQL_DATA,
	SAVINGS_SIGN_OFF_GRAPHQL_DATA,
	SPEND_CONTROLLER_BASELINE_FILTERS_STACK_1,
	SPEND_CONTROLLER_BASELINE_FILTERS_STACK_2,
	WORKSHOP_GRAPHQL_DATA,
	convertToFilterFormat,
	extractPathDetails,
} from 'SpendController/utils';
import React, { createContext, useContext, useEffect, useState } from 'react';
import {
	getDistinctValue,
	getSpendControllerFilters,
	getWorkshopId,
} from 'utils/apiRequests';

const SpendControllerFilters = createContext();

export const SpendControllerFiltersProvider = ({ children }) => {
	const [filters, setFilters] = useState({
		stack_1: SPEND_CONTROLLER_BASELINE_FILTERS_STACK_1,
		stack_2: SPEND_CONTROLLER_BASELINE_FILTERS_STACK_2,
	});
	const [baselineColumnDefs, setBaselineColumnDefs] = useState(
		BASELINE_GRAPHQL_DATA
	);
	const [preWorkShopColumnDefs, setPreWorkShopColumnDefs] = useState(
		PREWORKSHOP_GRAPHQL_DATA
	);
	const [workShopColumnDefs, setWorkShopColumnDefs] = useState(
		WORKSHOP_GRAPHQL_DATA
	);
	const [savingsSignOffColumnDefs, setSavingsSignOffColumnDefs] = useState(
		SAVINGS_SIGN_OFF_GRAPHQL_DATA
	);
	const [microLaunchColumnDefs, setMicroLaunchColumnDefs] = useState(
		MICRO_LAUNCH_GRAPHQL_DATA
	);
	const [filterModelData, setFilterModelData] = useState({});
	const [refresh, setRefresh] = useState(false);
	const [uploadRefresh, setUploadRefresh] = useState(false);
	const [searchOptions, setSearchOptions] = useState([]);
	const [selectedBreakDownFilter, setSelectedBreakDownFilter] = useState(1);
	const [spendType, setSpendType] = useState([]);
	const [workshopState, setWorkshopState] = useState([]);
	useEffect(
		() => {
			spendType.length > 0 && handleSpendControllerFilters();
		},
		// eslint-disable-next-line
		[spendType, uploadRefresh]
	);
	useEffect(
		() => {
			getWorkshopId().then(response => setWorkshopState(response.data));
		},
		[uploadRefresh]
	);

	useEffect(
		() => {
			const tempFilterModelData = convertToFilterFormat([
				...filters.stack_1,
				...filters.stack_2,
			]);
			setFilterModelData(tempFilterModelData);
		},
		[filters]
	);

	const handleResetFilters = () => {
		const updatedFilters = Object.keys(filters).reduce((acc, stackKey) => {
			acc[stackKey] = filters[stackKey].map(obj => ({
				...obj,
				selected: [],
			}));
			return acc;
		}, {});
		setFilters(updatedFilters);
	};
	const handleSpendControllerFilters = async () => {
		const payload = {
			tailSpendType: spendType,
		};
		const filterResponse = await getSpendControllerFilters(payload);
		const workshopIdNameResponse = await getWorkshopId();
		const distinctValuesResponse = await getDistinctValue(payload);
		const workshopNameOptions = workshopIdNameResponse.data.map(
			respObj => respObj.workshop_name
		);
		const fieldOptionsMap = {
			workshop_Name: workshopNameOptions,
			final_Spend_Owner_Name: distinctValuesResponse?.data?.spend_Owner || [],
		};

		const applyFilterValuesToColumns = columnDefs => {
			const updatedColumnDefs = columnDefs?.map(col => {
				let filterValues = distinctValuesResponse.data[col.field];
				if (col.filter === 'agSetColumnFilter') {
					filterValues = filterValues ? [...filterValues, 'NULL'] : ['NULL'];
				}

				if (col.children) {
					col.children = col.children.map(child => {
						let childFilterValues = distinctValuesResponse.data[child.field];
						if (child.filter === 'agSetColumnFilter') {
							childFilterValues = childFilterValues
								? [...childFilterValues, 'NULL']
								: ['NULL'];
						}

						if (fieldOptionsMap[child.field]) {
							child.cellEditorParams = child.cellEditorParams || {};
							child.cellEditorParams.values = fieldOptionsMap[child.field];

							child.valueSetter = params => {
								if (child.cellEditorParams.values.includes(params.newValue)) {
									const newVal = params.newValue;
									const valueChanged = params.data?.[child.field] !== newVal;
									if (valueChanged) {
										params.data[child.field] = newVal;
									}
									return valueChanged;
								}
								return false;
							};
						}

						return {
							...child,
							filterParams: childFilterValues
								? { values: childFilterValues }
								: child.filterParams,
						};
					});
				}

				if (fieldOptionsMap[col.field]) {
					col.cellEditorParams = col.cellEditorParams || {};
					col.cellEditorParams.values = fieldOptionsMap[col.field];

					col.valueSetter = params => {
						if (col.cellEditorParams.values.includes(params.newValue)) {
							const newVal = params.newValue;
							const valueChanged = params.data?.[col.field] !== newVal;
							if (valueChanged) {
								params.data[col.field] = newVal;
							}
							return valueChanged;
						}
						return false;
					};
				}

				return {
					...col,
					filterParams: filterValues
						? { values: filterValues }
						: col.filterParams,
				};
			});

			return updatedColumnDefs;
		};

		let filterKeys = Object.keys(filterResponse.data);
		if (
			filterResponse.data.businessUnit.length === 0 &&
			filterResponse.data.entity.length === 0 &&
			filterResponse.data.spendOwner.length === 0 &&
			filterResponse.data.finalCategory.length === 0 &&
			filterResponse.data.normalizedVendor.length === 0
		) {
			setFilters({
				stack_1: SPEND_CONTROLLER_BASELINE_FILTERS_STACK_1,
				stack_2: SPEND_CONTROLLER_BASELINE_FILTERS_STACK_2,
			});
			return;
		}
		const tempBaselineColumnDefs = baselineColumnDefs.columnDefs.map(data => {
			let filterValues = distinctValuesResponse.data[data.field];
			if (data.filter === 'agSetColumnFilter') {
				filterValues = filterValues ? [...filterValues, 'NULL'] : ['NULL'];
			}
			return {
				...data,
				filterParams: filterValues
					? {
							values: filterValues,
					  }
					: data.filterParams,
			};
		});
		tempBaselineColumnDefs.forEach(obj => {
			if (fieldOptionsMap[obj.field]) {
				obj.cellEditorParams.values = fieldOptionsMap[obj.field];

				obj.valueSetter = params => {
					if (obj.cellEditorParams.values.includes(params.newValue)) {
						const newVal = params.newValue;
						const valueChanged = params.data?.[obj.field] !== newVal;
						if (valueChanged) {
							params.data[obj.field] = newVal;
						}
						return valueChanged;
					}
					return false;
				};
			}
		});

		setBaselineColumnDefs({
			...baselineColumnDefs,
			columnDefs: tempBaselineColumnDefs,
		});
		// tempPreWorkShopColumnDefs[9].children[3].filterParams.values = [
		// 	null,
		// 	...tempPreWorkShopColumnDefs[9].children[3].filterParams.values,
		// ];
		setPreWorkShopColumnDefs({
			...preWorkShopColumnDefs,
			columnDefs: applyFilterValuesToColumns(preWorkShopColumnDefs.columnDefs),
		});

		setWorkShopColumnDefs({
			...workShopColumnDefs,
			columnDefs: applyFilterValuesToColumns(workShopColumnDefs.columnDefs),
		});
		const tempSavingsSignOffColumnDefs = savingsSignOffColumnDefs.columnDefs.map(
			data => {
				let filterValues = distinctValuesResponse.data[data.field];
				if (data.filter === 'agSetColumnFilter') {
					filterValues = filterValues ? [...filterValues, 'NULL'] : ['NULL'];
				}
				if (data.hasOwnProperty('children')) {
					const updatedChildren = data.children.map(child => {
						const childFilterValues = distinctValuesResponse.data[child.field];
						return {
							...child,
							filterParams: childFilterValues
								? {
										values: childFilterValues,
								  }
								: child.filterParams,
						};
					});

					return {
						...data,
						children: updatedChildren,
					};
				}
				return {
					...data,
					filterParams: filterValues
						? {
								values: filterValues,
						  }
						: data.filterParams,
				};
			}
		);
		setSavingsSignOffColumnDefs({
			...savingsSignOffColumnDefs,
			columnDefs: tempSavingsSignOffColumnDefs,
		});

		setMicroLaunchColumnDefs({
			...microLaunchColumnDefs,
			columnDefs: applyFilterValuesToColumns(microLaunchColumnDefs.columnDefs),
		});

		let tempFilters = filters;
		const tempSearchOptions = [];
		for (let i = 0; i < filterKeys.length; i++) {
			tempSearchOptions.push(
				...filterResponse.data[filterKeys[i]].map(data => ({
					name: data,
					value: data,
				}))
			);
		}

		tempSearchOptions.push(
			...workshopNameOptions.map(data => ({
				name: data,
				value: data,
			}))
		);
		const uniqueSearchOptions = Array.from(
			new Set(tempSearchOptions.map(option => option.value))
		).map(value => {
			return tempSearchOptions.find(option => option.value === value);
		});
		setSearchOptions(uniqueSearchOptions);
		for (let i = 0; i < 3; i++) {
			tempFilters.stack_1[i].options = filterResponse.data[filterKeys[i]].map(
				data => ({ label: data, value: data })
			);
			tempFilters.stack_1[i].onChange = handleFilterValuesSelected;
		}
		for (let i = 3; i < filterKeys.length; i++) {
			tempFilters.stack_2[i - 3].options = filterResponse.data[
				filterKeys[i]
			].map(data => ({ label: data, value: data }));
			tempFilters.stack_2[i - 3].onChange = handleFilterValuesSelected;
		}

		setFilters(tempFilters);
	};
	const handleFilterValuesSelected = (key, value) => {
		const tempFilters = { ...filters };
		const { objectName, number, valueAfterDot } = extractPathDetails(key);
		tempFilters[objectName][number][valueAfterDot] = value;
		setFilters(tempFilters);
	};
	// useEffect(
	// 	() => {
	// 		// setFilters({
	// 		// 	stack_1: SPEND_CONTROLLER_BASELINE_FILTERS_STACK_1,
	// 		// 	stack_2: SPEND_CONTROLLER_BASELINE_FILTERS_STACK_2,
	// 		// });
	// 		handleSpendControllerFilters();
	// 	},
	// 	// eslint-disable-next-line react-hooks/exhaustive-deps
	// 	[uploadRefresh]
	// );
	const handleBreakDownFilterChange = value => {
		setSelectedBreakDownFilter(value);
	};

	return (
		<SpendControllerFilters.Provider
			value={{
				isLoading: false,
				filters,
				baselineColumnDefs,
				handleSpendControllerFilters,
				preWorkShopColumnDefs,
				workShopColumnDefs,
				savingsSignOffColumnDefs,
				filterModelData,
				setRefresh,
				refresh,
				uploadRefresh,
				setUploadRefresh,
				searchOptions,
				selectedBreakDownFilter,
				handleBreakDownFilterChange,
				workshopState,
				setWorkshopState,
				setSpendType,
				spendType,
				microLaunchColumnDefs,
				handleResetFilters,
			}}
		>
			{children}
		</SpendControllerFilters.Provider>
	);
};

export const useSpendControllerFiltersContext = () => {
	return useContext(SpendControllerFilters);
};
